import { FC, useCallback, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import {
  CircleCheckIcon,
  CloudUploadIcon,
  CrossIcon,
  DownloadIcon,
  FileAttachment,
  ImportIcon,
  InfoCircle,
  UploadIcon,
} from '../../../../../../../assets/icons/index';
import LoaderIcon from '../../../../../../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../../components/button/index';
import { HttpException } from '../../../../../../../exceptions/index';
import { useUploadFileToS3 } from '../../../../../../../hooks/index';
import { PageRoute } from '../../../../../../../types/page.types';
import { formatBytesPrecise } from '../../../../../../../utils/format-bytes-precise.util';
import { getTranslation } from '../../../../../../../utils/get-translation.util';
import { ModifyGuideTab } from '../../../../../../modify-guide/modify-guide.types';
import { PopupType } from '../../../../enums/popup-type.const';
import { TemplatePopupStore } from '../../../../store/template-popup.store';
import { StepContentProps } from '../../../../types/template.types';
import {
  analyzeDocxMergeFields,
  DocxAnalysisFileTooLargeError,
  DocxAnalysisInvalidDocxError,
} from '../../../../utils/analyze-docx-merge-fields.util';
import { clearStepThreeMapping } from '../../../../utils/clear-step-three-mapping.util';
import { cloneStepThreeSnapshot } from '../../../../utils/clone-step-three-snapshot.util';
import {
  ADD_TEMPLATE_STEP_THREE_MAX_FILE_BYTES,
  getStepThreeFile,
  getStepThreeFileLink,
} from '../../../../validations/add-template.validation';
import { MergeFieldIssuesBanner } from '../../../merge-field-issues-banner';

type StepThreeProps = StepContentProps;

const ACCEPTED_FORMATS = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

const STANDARD_GRANT_AGREEMENT_TEMPLATE_FILENAME = 'Grant_Agreement_Template.docx';
const STANDARD_GRANT_AGREEMENT_TEMPLATE_HREF = `/${STANDARD_GRANT_AGREEMENT_TEMPLATE_FILENAME}`;

const txtTooLarge = (): string => getTranslation('FILE_SIZE_TOO_LARGE');
const txtWrongFormat = (): string => getTranslation('ONLY_DOCX_ALLOWED');
const txtUploadFailed = (): string => getTranslation('UNEXPECTED_ERROR_OCCURRED');

const messageForUploadFailure = (err: unknown): string => {
  if (err instanceof HttpException) {
    const { statusCode } = err.body;
    if (statusCode === 413) {
      return txtTooLarge();
    }
    if (statusCode === 415) {
      return txtWrongFormat();
    }
    return txtUploadFailed();
  }
  return txtUploadFailed();
};

const clearFileAndMappingState = (
  setValue: StepContentProps['setValue'],
  clearErrors: StepContentProps['clearErrors'],
): void => {
  setValue('stepThree.file', undefined, { shouldDirty: true, shouldValidate: true });
  clearStepThreeMapping(setValue);
  clearErrors?.('stepThree.file');
};

export const StepThree: FC<StepThreeProps> = ({
  clearErrors,
  control,
  getValues,
  setError,
  setValue,
}) => {
  const [isDocAnalysisInFlight, setIsDocAnalysisInFlight] = useState(false);
  const fileValue = useWatch({ control, name: 'stepThree.file' });
  const mergeFieldIssues =
    useWatch({ control, name: 'stepThree.mergeFieldIssues', defaultValue: [] }) ?? [];
  const uploadedFile = getStepThreeFile(fileValue);
  const { fieldState: fileFieldState } = useController({ control, name: 'stepThree.file' });
  const openPopup = TemplatePopupStore(({ openPopup }) => openPopup);
  const { uploadFile } = useUploadFileToS3();

  const docLink = getStepThreeFileLink(fileValue);
  const isUploaded = Boolean(docLink);
  const filePayload =
    fileValue && typeof fileValue === 'object' && 'doc' in fileValue
      ? (fileValue as { abort?: () => void; docLink?: string; loadProgress?: number })
      : null;
  const loadProgress = filePayload?.loadProgress ?? 0;
  const isS3UploadInFlight =
    Boolean(uploadedFile) &&
    !isUploaded &&
    !fileFieldState.error &&
    typeof filePayload?.abort === 'function' &&
    loadProgress < 100;
  const isLoadingPanel = isDocAnalysisInFlight || isS3UploadInFlight;
  const hasError = Boolean(fileFieldState.error) && !isLoadingPanel;

  const panelState: 'error' | 'idle' | 'loading' | 'success' = (() => {
    if (isLoadingPanel) return 'loading';
    if (isUploaded && uploadedFile) return 'success';
    if (hasError) return 'error';
    return 'idle';
  })();

  const showFileRow = panelState === 'success' && Boolean(uploadedFile);

  const onSelectFile = useCallback(
    async (file: File) => {
      if (file.size > ADD_TEMPLATE_STEP_THREE_MAX_FILE_BYTES) {
        setError('stepThree.file', {
          type: 'manual',
          message: txtTooLarge(),
        });
        return;
      }
      if (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        setError('stepThree.file', {
          type: 'manual',
          message: txtWrongFormat(),
        });
        return;
      }

      clearErrors('stepThree.file');
      clearStepThreeMapping(setValue);

      setValue(
        'stepThree.file',
        { doc: file, docLink: '', loadProgress: 0 },
        { shouldDirty: true, shouldValidate: true },
      );
      setIsDocAnalysisInFlight(true);

      let detectedVariables: string[] = [];
      try {
        const analysis = await analyzeDocxMergeFields(file);
        detectedVariables = analysis.variables;
        if (analysis.issues.length > 0) {
          setIsDocAnalysisInFlight(false);
          setValue('stepThree.detectedVariables', analysis.variables, { shouldValidate: true });
          setValue('stepThree.mergeFieldIssues', analysis.issues, { shouldValidate: true });
          setValue('stepThree.fileParsed', false, { shouldValidate: true });
          setError('stepThree.file', {
            type: 'manual',
            message: getTranslation('MERGE_FIELD_ISSUES_BLOCK_SUBMIT'),
          });
          return;
        }
      } catch (err) {
        setIsDocAnalysisInFlight(false);
        setValue('stepThree.detectedVariables', [], { shouldValidate: true });
        setValue('stepThree.mergeFieldIssues', [], { shouldValidate: true });
        const tooLarge = err instanceof DocxAnalysisFileTooLargeError;
        const invalidDocx = err instanceof DocxAnalysisInvalidDocxError;
        setError('stepThree.file', {
          type: 'manual',
          message: tooLarge ? txtTooLarge() : invalidDocx ? txtWrongFormat() : txtUploadFailed(),
        });
        return;
      }

      try {
        await uploadFile(file, {
          setUploading: (abort) => {
            setIsDocAnalysisInFlight(false);
            setValue(
              'stepThree.file',
              { doc: file, docLink: '', loadProgress: 0, abort },
              { shouldDirty: true, shouldValidate: true },
            );
          },
          setProgress: (n, abort) => {
            setValue(
              'stepThree.file',
              { doc: file, docLink: '', loadProgress: n, abort },
              { shouldDirty: true, shouldValidate: true },
            );
          },
          setSuccess: (link, abort) => {
            setValue(
              'stepThree.file',
              { doc: file, docLink: link, loadProgress: 100, abort },
              { shouldDirty: true, shouldValidate: true },
            );
            setValue('stepThree.detectedVariables', detectedVariables, { shouldValidate: true });
            setValue('stepThree.mergeFieldIssues', [], { shouldValidate: true });
            setValue('stepThree.fileParsed', true, { shouldValidate: true });
          },
          setAbort: () =>
            setValue('stepThree.file', undefined, { shouldDirty: true, shouldValidate: true }),
        });
      } catch (err) {
        setIsDocAnalysisInFlight(false);
        clearStepThreeMapping(setValue);
        setValue('stepThree.file', undefined, { shouldDirty: true, shouldValidate: false });
        setError('stepThree.file', {
          type: 'manual',
          message: messageForUploadFailure(err),
        });
      }
    },
    [clearErrors, setError, setValue, uploadFile],
  );

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await onSelectFile(file);
    event.target.value = '';
  };

  const handleRemoveFile = useCallback(() => {
    if (fileValue && typeof fileValue === 'object' && 'abort' in fileValue && !fileValue.docLink) {
      fileValue.abort?.();
    }
    clearFileAndMappingState(setValue, clearErrors);
  }, [clearErrors, fileValue, setValue]);

  const handleUploadAgain = (e: React.MouseEvent) => {
    e.stopPropagation();
    document.getElementById('template-file-input')?.click();
  };

  const handleMapFields = () => {
    openPopup({
      mapFieldsStepThreeSnapshot: cloneStepThreeSnapshot(getValues('stepThree')),
      onAcceptCallback: () => {},
      type: PopupType.MAP_FIELDS,
    });
  };

  const handleDownloadStandardTemplate = useCallback(() => {
    const link = document.createElement('a');
    link.href = STANDARD_GRANT_AGREEMENT_TEMPLATE_HREF;
    link.download = STANDARD_GRANT_AGREEMENT_TEMPLATE_FILENAME;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  const errorMessage = fileFieldState.error?.message ?? txtUploadFailed();
  const showMaxSizeHint = errorMessage === txtTooLarge();

  return (
    <div className="flex h-full flex-col bg-gray-100">
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-4 pt-6">
        <div className="flex flex-col items-center gap-1 rounded-lg bg-gray-50 p-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-700">
              <AppFormattedMessage id="DOWNLOAD_SAMPLE_TEMPLATE" />
            </span>
            <span className="text-label-sm text-gray-400">
              <AppFormattedMessage id="USE_PREDEFINED_TEMPLATE" />
            </span>
          </div>
          <Button
            className="flex h-11 w-full shrink-0 gap-2 border border-gray-100 bg-white px-3 py-2 text-sm text-gray-700 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
            onClick={handleDownloadStandardTemplate}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <DownloadIcon className="h-4 w-4" />
            <AppFormattedMessage id="DOWNLOAD_TEMPLATE" />
          </Button>
        </div>

        <Link
          className="flex w-fit items-center gap-2 text-sm font-[450] text-brand-700 underline hover:text-brand-800"
          rel="noopener noreferrer"
          target="_blank"
          to={`${PageRoute.GUIDES}/${ModifyGuideTab.TEMPLATE_PREPARATION}`}
        >
          <AppFormattedMessage id="HOW_TO_PREPARE_TEMPLATE" />
          <ImportIcon />
        </Link>

        <div className="flex flex-col gap-4">
          <span className="text-xs text-gray-500">
            <AppFormattedMessage id="DOCUMENT_UPLOAD" />
          </span>

          <div
            className={twMerge(
              'flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-gray-200 bg-white p-6 transition-colors',
              panelState === 'error' && 'bg-fireside-25',
            )}
          >
            <input
              accept={Object.keys(ACCEPTED_FORMATS).join(',')}
              className="hidden"
              disabled={isLoadingPanel}
              id="template-file-input"
              onChange={handleInputChange}
              type="file"
            />

            {panelState === 'idle' && (
              <>
                <CloudUploadIcon className="h-12 w-12" />
                <Button
                  className="gap-2 border border-gray-100 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={handleUploadAgain}
                  styleType="DEFAULT_ROUNDED"
                  type="button"
                >
                  <UploadIcon className="h-4 w-4" iconColor="#344054" />
                  <AppFormattedMessage id="CLICK_TO_UPLOAD" />
                </Button>
                <span className="text-xs text-gray-400">
                  <AppFormattedMessage id="SUPPORTED_FORMAT_TEMPLATES" />
                </span>
                <span className="text-xs text-gray-400">
                  <AppFormattedMessage id="MAX_FILE_SIZE_10_MB" />
                </span>
              </>
            )}

            {panelState === 'loading' && <LoaderIcon className="h-12 w-12" />}

            {panelState === 'success' && uploadedFile && isUploaded && (
              <>
                <CircleCheckIcon className="h-12 w-12" />
                <span className="text-sm font-medium text-forest-600">
                  <AppFormattedMessage id="FILE_PROCESSED_SUCCESSFULLY" />
                </span>
                <span
                  className="w-full max-w-full truncate px-2 text-center text-xs text-gray-500"
                  title={uploadedFile.name}
                >
                  {uploadedFile.name}
                </span>
                <span className="text-xs text-gray-400">
                  {formatBytesPrecise(uploadedFile.size)}
                </span>
              </>
            )}

            {panelState === 'error' && (
              <>
                <InfoCircle className="h-12 w-12 shrink-0" iconColor="#D92D20" thinIconVariant />
                <span className="text-center text-sm font-medium text-fireside-600">
                  {errorMessage}
                </span>
                {uploadedFile && (
                  <span
                    className="w-full max-w-full truncate px-2 text-center text-xs text-gray-500"
                    title={uploadedFile.name}
                  >
                    {uploadedFile.name}
                  </span>
                )}
                {showMaxSizeHint && (
                  <span className="text-[10px] font-normal text-gray-500">
                    <AppFormattedMessage id="MAX_FILE_SIZE_10_MB" />
                  </span>
                )}
                <Button
                  className="mt-1 gap-2 border border-gray-100 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={handleUploadAgain}
                  styleType="DEFAULT_ROUNDED"
                  type="button"
                >
                  <UploadIcon className="h-4 w-4" iconColor="#344054" />
                  <AppFormattedMessage id="UPLOAD_AGAIN" />
                </Button>
              </>
            )}
          </div>

          {mergeFieldIssues.length > 0 && <MergeFieldIssuesBanner issues={mergeFieldIssues} />}

          {showFileRow && uploadedFile && (
            <div className="flex h-10 items-center gap-3 py-3">
              <div className="flex h-10 min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-lg border border-brand-50 bg-brand-25 px-3 py-0">
                <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                  <FileAttachment className="h-5 w-5 shrink-0" />
                  <span className="truncate text-sm text-gray-700">{uploadedFile.name}</span>
                </div>
                <button
                  className="ml-auto shrink-0 rounded p-1 hover:bg-gray-100 disabled:opacity-40"
                  onClick={handleRemoveFile}
                  type="button"
                >
                  <CrossIcon className="h-4 w-4" />
                </button>
              </div>
              <Button
                className="h-10 shrink-0 bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-100 disabled:text-gray-300"
                disabled={mergeFieldIssues.length > 0}
                onClick={handleMapFields}
                styleType="DEFAULT_ROUNDED"
                type="button"
              >
                <AppFormattedMessage id="MAP_FIELDS" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
